import { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Logo } from "@/components/Logo";
import { ThemeToggle } from "@/components/ThemeToggle";
import { SystemStatus } from "@/components/SystemStatus";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { LangCurrencySwitcher } from "@/components/LangCurrencySwitcher";
import {
  LayoutDashboard, LineChart, Radar, Bot, Wallet, History, Calculator,
  Star, Bell, Gift, Settings, Shield, LogOut, Menu,
} from "lucide-react";

const nav = [
  { to: "/app", key: "nav.dashboard", icon: LayoutDashboard, end: true },
  { to: "/app/markets", key: "nav.markets", icon: LineChart },
  { to: "/app/signals", key: "nav.signals", icon: Radar },
  { to: "/app/analyst", key: "nav.aiAnalyst", icon: Bot },
  { to: "/app/paper-trading", key: "nav.paperTrading", icon: Wallet },
  { to: "/app/backtesting", key: "nav.backtesting", icon: History },
  { to: "/app/risk", key: "nav.riskManager", icon: Calculator },
  { to: "/app/watchlist", key: "nav.watchlist", icon: Star },
  { to: "/app/alerts", key: "nav.alerts", icon: Bell },
  { to: "/app/referrals", key: "nav.referrals", icon: Gift },
  { to: "/app/account", key: "nav.settings", icon: Settings },
];

const bottomNav = [
  { to: "/app", key: "nav.home", icon: LayoutDashboard, end: true },
  { to: "/app/signals", key: "nav.signals", icon: Radar },
  { to: "/app/analyst", key: "nav.aiAnalyst", icon: Bot },
  { to: "/app/paper-trading", key: "nav.trade", icon: Wallet },
  { to: "/app/risk", key: "nav.riskManager", icon: Calculator },
];

export function DashboardLayout({ children, title }) {
  const { user, logout } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  useEffect(() => { document.title = title ? `${title} · KREONIS` : "KREONIS"; }, [title]);
  useEffect(() => {
    const pending = sessionStorage.getItem("kreonis_pending_checkout");
    if (!pending) return;
    sessionStorage.removeItem("kreonis_pending_checkout");
    try {
      const { lookup, name } = JSON.parse(pending);
      toast.info(`Resuming checkout for ${name}…`);
      api.post("/billing/checkout", { lookup_key: lookup, origin_url: window.location.origin })
        .then(({ data }) => { window.location.href = data.checkout_url; })
        .catch((e) => toast.error(e.response?.data?.detail || "Checkout unavailable"));
    } catch { /* ignore malformed intent */ }
  }, []);
  const isAdmin = ["admin", "super_admin"].includes(user?.role);

  const doLogout = async () => { await logout(); navigate("/"); };

  return (
    <div className="min-h-screen bg-background">
      {/* Sidebar - desktop */}
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-60 flex-col border-r border-border bg-card lg:flex">
        <div className="flex h-16 items-center border-b border-border px-5"><Logo /></div>
        <nav className="flex-1 overflow-y-auto p-3 no-scrollbar">
          {nav.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} data-testid={`side-${n.to.split("/").pop() || "dashboard"}`}
              className={({ isActive }) => `mb-1 flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${isActive ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-secondary hover:text-foreground"}`}>
              <n.icon className="h-4 w-4" /> {t(n.key)}
            </NavLink>
          ))}
          {isAdmin && (
            <NavLink to="/admin" data-testid="side-admin" className={({ isActive }) => `mb-1 flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${isActive ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-secondary hover:text-foreground"}`}>
              <Shield className="h-4 w-4" /> {t("nav.admin")}
            </NavLink>
          )}
        </nav>
        <div className="border-t border-border p-3">
          <SystemStatus />
          <button onClick={doLogout} data-testid="logout-btn" className="mt-3 flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-muted-foreground hover:bg-secondary hover:text-foreground">
            <LogOut className="h-4 w-4" /> {t("nav.logout")}
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="lg:pl-60">
        <header className="sticky top-0 z-30 flex h-16 items-center justify-between border-b border-border glass px-4 sm:px-6">
          <div className="flex items-center gap-3">
            <button className="lg:hidden" onClick={() => setOpen(!open)} data-testid="mobile-side-toggle"><Menu className="h-5 w-5" /></button>
            <h1 className="font-display text-lg font-bold tracking-tight" data-testid="page-title">{title}</h1>
          </div>
          <div className="flex items-center gap-3">
            <div className="hidden sm:block"><LangCurrencySwitcher compact /></div>
            <SystemStatus inline />
            <ThemeToggle />
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center text-xs font-bold text-white">
                {user?.name?.[0]?.toUpperCase() || "U"}
              </div>
              <span className="hidden text-sm font-medium sm:block">{user?.name}</span>
              <span className="hidden rounded-md bg-secondary px-1.5 py-0.5 text-[10px] font-semibold uppercase text-muted-foreground sm:block">{user?.plan}</span>
            </div>
          </div>
        </header>

        {open && (
          <div className="border-b border-border bg-card lg:hidden" data-testid="mobile-side-menu">
            <nav className="grid grid-cols-2 gap-1 p-3">
              {nav.map((n) => (
                <NavLink key={n.to} to={n.to} end={n.end} onClick={() => setOpen(false)}
                  className={({ isActive }) => `flex items-center gap-2 rounded-lg px-3 py-2.5 text-sm ${isActive ? "bg-primary/10 text-primary" : "text-muted-foreground"}`}>
                  <n.icon className="h-4 w-4" /> {t(n.key)}
                </NavLink>
              ))}
              {isAdmin && <NavLink to="/admin" onClick={() => setOpen(false)} className="flex items-center gap-2 rounded-lg px-3 py-2.5 text-sm text-muted-foreground"><Shield className="h-4 w-4" /> {t("nav.admin")}</NavLink>}
              <button onClick={doLogout} className="flex items-center gap-2 rounded-lg px-3 py-2.5 text-sm text-muted-foreground"><LogOut className="h-4 w-4" /> {t("nav.logout")}</button>
            </nav>
            <div className="border-t border-border p-3"><LangCurrencySwitcher /></div>
          </div>
        )}

        <main className="mx-auto max-w-7xl p-4 pb-24 sm:p-6 lg:pb-6">{children}</main>
      </div>

      {/* Bottom nav - mobile */}
      <nav className="fixed inset-x-0 bottom-0 z-40 flex items-center justify-around border-t border-border glass py-2 lg:hidden" data-testid="mobile-bottom-nav">
        {bottomNav.map((n) => (
          <NavLink key={n.to} to={n.to} end={n.end}
            className={({ isActive }) => `flex flex-col items-center gap-0.5 px-2 py-1 text-[10px] font-medium ${isActive ? "text-primary" : "text-muted-foreground"}`}>
            <n.icon className="h-5 w-5" /> {t(n.key)}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
